import { useEffect, useState } from "react";
import { BrowserRouter, useLocation, useNavigate, useParams, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { http } from "./api";
import Login from "./Login";
import Shell from "./Shell";
import LeaderboardTV from "./pages/LeaderboardTV";
import PublicForm from "./pages/PublicForm";
import PublicWardWall from "./pages/PublicWardWall";
import VoterJourney from "./pages/VoterJourney";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import "./App.css";
import "./login.css";
import "./modules.css";

function AuthCallback({ onLogin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  useEffect(() => {
    const hash = location.hash || window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const sid = m[1];
    // clear hash from URL immediately
    window.history.replaceState(null, "", window.location.pathname);
    http.post("/auth/google/session", { session_id: sid })
      .then((r) => { onLogin(r.data); navigate("/", { replace: true }); })
      .catch(() => setError("Google sign-in failed. Please try again."));
  }, [location.hash, navigate, onLogin]);
  return (
    <div className="loading" data-testid="auth-callback-screen">
      <div className="mark">84</div>
      <p>{error || "Completing Google sign-in…"}</p>
    </div>
  );
}

function AppInner() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [branding, setBranding] = useState(null);
  const location = useLocation();

  const hasSession = (location.hash || window.location.hash || "").includes("session_id=");
  const isTvRoute = location.pathname === "/tv" || location.pathname === "/leaderboard-tv";
  const isPublicForm = location.pathname.startsWith("/f/");
  const isPublicWall = location.pathname.startsWith("/public/");
  const isJourneyDeepLink = location.pathname.startsWith("/journey/");
  const isAppRoute = location.pathname.startsWith("/app");
  const isLoginRoute = location.pathname === "/login" || location.pathname === "/signin";
  const isPricingRoute = location.pathname === "/pricing";
  // Landing at "/" is public — CRM lives at /app after login.
  const isLandingRoute = location.pathname === "/" || location.pathname === "/home";

  useEffect(() => {
    if (hasSession || isPublicForm || isPublicWall) { setChecking(false); return; }
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false));
  }, [hasSession, isPublicForm, isPublicWall]);

  useEffect(() => {
    http.get("/branding/effective").then((r) => setBranding(r.data || {})).catch(() => setBranding({}));
  }, []);

  if (hasSession) return <AuthCallback onLogin={setUser} />;
  if (isPublicForm) return <Routes><Route path="/f/:slug" element={<PublicForm />} /></Routes>;
  if (isPublicWall) return <Routes><Route path="/public/:slug" element={<PublicWardWall />} /></Routes>;
  if (checking || branding === null) return <div className="loading"><div className="mark">84</div><p>Opening Nabadwip command center…</p></div>;

  // Public pricing page — always accessible.
  if (isPricingRoute) return <Pricing />;

  // Global "landing_mode": Super Admin can force anonymous visitors straight to login.
  if (isLandingRoute && !user && branding?.landing_mode === "login_default") {
    return <Login onLogin={setUser} />;
  }

  // Landing page (public) — shown on / and /home regardless of login.
  // If a staff/admin user is logged in on landing, they can tap "Open CRM".
  if (isLandingRoute) return <Landing user={user || null} onLogin={setUser} />;

  if (!user) {
    // Preserve deep-link destination if user needs to log in first.
    if (isJourneyDeepLink) {
      try { sessionStorage.setItem("ward7crm.postLoginPath", location.pathname); } catch { /* noop */ }
    }
    if (isLoginRoute || isAppRoute) return <Login onLogin={setUser} />;
    // Everything else redirects to landing
    window.history.replaceState(null, "", "/");
    return <Landing user={null} onLogin={setUser} />;
  }
  if (isTvRoute) return <LeaderboardTV />;
  if (isJourneyDeepLink) return <Routes><Route path="/journey/:contactId" element={<JourneyDeepLink user={user} />} /></Routes>;
  // Logged-in Public users must stay on landing (they don't have CRM access)
  if (user.role === "Public") {
    window.history.replaceState(null, "", "/");
    return <Landing user={user} onLogin={setUser} />;
  }
  return <Shell user={user} setUser={setUser} />;
}

function JourneyDeepLink({ user }) {
  const { contactId } = useParams();
  const navigate = useNavigate();
  return (
    <div className="modal-backdrop" style={{ background: "#0f2a2b" }} onClick={(e) => { if (e.target === e.currentTarget) navigate("/"); }}>
      <VoterJourney contactId={contactId} user={user} onClose={() => navigate("/")} />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
      <Toaster position="top-right" richColors closeButton toastOptions={{ duration: 6000 }} />
    </BrowserRouter>
  );
}
